"use client";

import * as React from "react";
import * as RToast from "@radix-ui/react-toast";
import { CheckCircle2, Info, AlertTriangle, X } from "lucide-react";
import { cn } from "./cn";

export type ToastTone = "default" | "success" | "error" | "warning";

type ToastItem = {
  id: string;
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number;
};

type ToastContextValue = {
  toast: (item: Omit<ToastItem, "id">) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const push = React.useCallback((item: Omit<ToastItem, "id">) => {
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `t-${Date.now()}-${Math.random()}`;
    setItems((prev) => [...prev, { ...item, id }]);
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({
      toast: push,
      success: (title, description) =>
        push({ title, description, tone: "success" }),
      error: (title, description) =>
        push({ title, description, tone: "error" }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      <RToast.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastCard
            key={item.id}
            item={item}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id);
            }}
          />
        ))}
        <RToast.Viewport className="fixed bottom-4 right-4 z-60 flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto outline-none" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

function ToastCard({
  item,
  onOpenChange,
}: {
  item: ToastItem;
  onOpenChange: (open: boolean) => void;
}) {
  const icon =
    item.tone === "success" ? (
      <CheckCircle2 size={18} className="text-positive shrink-0" />
    ) : item.tone === "error" ? (
      <AlertTriangle size={18} className="text-danger shrink-0" />
    ) : item.tone === "warning" ? (
      <AlertTriangle size={18} className="text-warning shrink-0" />
    ) : (
      <Info size={18} className="text-info shrink-0" />
    );

  return (
    <RToast.Root
      duration={item.durationMs ?? 3500}
      onOpenChange={onOpenChange}
      className={cn(
        "bg-paper rounded-[16px] shadow-lg border-[1.5px] border-smoke px-4 py-3",
        "flex items-start gap-3 w-full",
        "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out",
      )}
    >
      {icon}
      <div className="flex-1 min-w-0">
        <RToast.Title className="text-sm font-bold text-ink">
          {item.title}
        </RToast.Title>
        {item.description ? (
          <RToast.Description className="text-xs text-ash mt-0.5">
            {item.description}
          </RToast.Description>
        ) : null}
      </div>
      <RToast.Close
        aria-label="Đóng"
        className="shrink-0 text-ash hover:text-ink transition-colors"
      >
        <X size={14} />
      </RToast.Close>
    </RToast.Root>
  );
}
